import { FaWrench } from "react-icons/fa";
import Swal from "sweetalert2";
import { useReconcileStaleStripePaymentIntentsMutation } from "../billing-exceptions.service";

type Props = {
  isDarkMode: boolean;
  onComplete: () => Promise<unknown>;
};

const getErrorMessage = (error: unknown) => {
  if (typeof error !== "object" || error === null) {
    return "Unable to reconcile Stripe payment follow-ups.";
  }

  const data = "data" in error ? error.data : undefined;
  const dataError =
    typeof data === "object" && data !== null && "error" in data
      ? data.error
      : undefined;

  return (
    (typeof data === "string" && data) ||
    (typeof dataError === "string" && dataError) ||
    ("message" in error &&
      typeof error.message === "string" &&
      error.message) ||
    "Unable to reconcile Stripe payment follow-ups."
  );
};

export const ReconcileStripePaymentFollowUps = ({
  isDarkMode,
  onComplete,
}: Props) => {
  const [reconcileStaleStripePaymentIntents, { isLoading }] =
    useReconcileStaleStripePaymentIntentsMutation();

  const handleReconcile = async () => {
    const confirmation = await Swal.fire({
      title: "Reconcile Stripe payment follow-ups?",
      text: "This checks up to 300 stale Stripe payments against Stripe. Confirmed successes use the existing fulfillment flow, confirmed cancellations are updated, and pending payments remain unchanged.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0284c7",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Reconcile payments",
    });

    if (!confirmation.isConfirmed) return;

    Swal.fire({
      title: "Reconciling Stripe payments...",
      text: "This can take a few minutes.",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const result = await reconcileStaleStripePaymentIntents().unwrap();
      await onComplete();

      await Swal.fire({
        title: "Reconciliation finished",
        text: `Scanned ${result.scanned}. Repaired: ${result.succeeded} succeeded, ${result.canceled} canceled. ${result.stillPending} still pending, ${result.skipped} skipped, ${result.failed} failed.`,
        icon: result.failed ? "warning" : "success",
        confirmButtonColor: "#0284c7",
      });
    } catch (error) {
      await Swal.fire({
        title: "Reconciliation failed",
        text: getErrorMessage(error),
        icon: "error",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const buttonClassName = isDarkMode
    ? "border-sky-400/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
    : "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100";

  return (
    <button
      type="button"
      onClick={handleReconcile}
      disabled={isLoading}
      className={`inline-flex h-10 items-center justify-center gap-2 rounded border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
    >
      <FaWrench />
      {isLoading ? "Reconciling..." : "Reconcile Stripe payments"}
    </button>
  );
};
